import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Button,
  CardContent,
  Card,
  Typography,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import qs from "qs";
import { useDispatch } from "react-redux";
import { setSubmitted, selectedProducts } from "redux/productParams";
import VirtualizedMultiSelect from "components/General/VirtualizedMultiSelect";

const FALLBACK_GROUP_MAP = {
  MB: ["5"],
  NB: ["22"],
  NL: ["22"],
  NS: ["22"],
  ON: ["1", "2", "5", "26"],
  QC: ["23", "24", "25", "26"],
  SK: ["2"],
};

function applyGroups(map, apply) {
  const g = Object.entries(map).map(([label, opts]) => ({
    label,
    options: opts,
  }));
  apply(
    map,
    g,
    g.length ? Array.from(new Set(g.flatMap((x) => x.options))) : [],
  );
}

export default function ProductFilterForm({ sendDataToParent }) {
  const [products, setProducts] = useState([]);
  const [productselected, setProductselected] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);
  const [allStoresOptions, setAllStoresOptions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupMap, setGroupMap] = useState({});
  const [allDistricts, setAllDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const dispatcher = useDispatch();

  useEffect(() => {
    axios.get("/api/productsDetail").then((res) => setProducts(res.data));
    axios
      .get("/api/storesDetail", {
        params: { storeNumber: true },
      })
      .then((res) => {
        const list = res.data.nameNumber || res.data;
        setStoresOptions(list);
        setAllStoresOptions(list);
        setSelectedStores(list);
      });
    axios
      .get("/api/provinceDistrictsGrouped")
      .then((res) => {
        applyGroups(res.data || {}, (map, g, all) => {
          setGroupMap(map);
          setGroups(g);
          setAllDistricts(all);
          setSelectedDistricts(all);
        });
      })
      .catch(() => {
        applyGroups(FALLBACK_GROUP_MAP, (map, g, all) => {
          setGroupMap(map);
          setGroups(g);
          setAllDistricts(all);
          setSelectedDistricts(all);
        });
      });
  }, []);

  useEffect(() => {
    if (!groups.length) return;
    if (selectedDistricts.length === 0) {
      setStoresOptions([]);
      setSelectedStores([]);
      return;
    }
    if (selectedDistricts.length === allDistricts.length) {
      setStoresOptions(allStoresOptions);
      return;
    }
    const fetchFiltered = async () => {
      try {
        const res = await axios.get("/api/districtStoresDetail", {
          params: { districtId: selectedDistricts },
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
        });
        setStoresOptions(res.data);
        setSelectedStores([]);
      } catch (e) {
        console.error("Error fetching filtered stores:", e);
      }
    };
    if (allStoresOptions.length) fetchFiltered();
  }, [selectedDistricts, groups, allDistricts, allStoresOptions]);

  useEffect(() => {
    setSubmitEnabled(productselected.length > 0);
  }, [productselected]);

  const productOptions = products.map(
    (p) => `${p.product_id} - ${p.product_name}`,
  );

  const handleProductChange = (event) => {
    let value = event.target.value || [];
    if (value.includes("all")) {
      const isAll = productselected.length === productOptions.length;
      value = isAll ? [] : [...productOptions];
    } else {
      value = value.filter((v) => v !== "all");
    }
    setProductselected(value);
    dispatcher(
      selectedProducts(
        value.map((item) => item.substring(0, item.indexOf(" "))),
      ),
    );
  };

  const handleStoresChange = (event) => {
    let value = event.target.value || [];
    if (value.includes("all")) {
      const isAll =
        selectedStores.length === storesOptions.length &&
        storesOptions.length > 0;
      value = isAll ? [] : [...storesOptions];
    } else {
      value = value.filter((v) => v !== "all");
    }
    setSelectedStores(value);
  };

  const handleDistrictsChange = (event) => {
    const value = (event.target.value || []).filter((v) => v !== "all");
    setSelectedDistricts(value);
  };

  const handleClearGeography = () => {
    setSelectedDistricts([...allDistricts]);
  };

  const geographyActive =
    groups.length > 0 && selectedDistricts.length !== allDistricts.length;

  function handleSubmit(event) {
    event.preventDefault();
    dispatcher(setSubmitted(true));
    const provinces = Object.keys(groupMap).filter((p) =>
      groupMap[p].some((d) => selectedDistricts.includes(d)),
    );
    sendDataToParent({
      startDate,
      endDate,
      stores: selectedStores,
      allStores:
        selectedStores.length === storesOptions.length &&
        storesOptions.length > 0,
      districts: selectedDistricts,
      provinces,
    });
  }

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Box ml={3} mr={3}>
        <Card>
          <CardContent>
            <Box pt={2} pb={2} ml={1}>
              <Grid container spacing={2}>
                <Grid item md={3} xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item md={3} xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Stores{" "}
                      {geographyActive && (
                        <Chip
                          label="Filtered by geography"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <VirtualizedMultiSelect
                      label="Stores"
                      id="stores-virtualized"
                      value={selectedStores}
                      options={storesOptions}
                      onChange={handleStoresChange}
                      width={600}
                      showSelectAll
                    />
                  </Box>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Filter stores by geography (optional)
                    </Typography>
                    <VirtualizedMultiSelect
                      label="Store districts"
                      id="districts-virtualized"
                      value={selectedDistricts}
                      groups={groups}
                      onChange={handleDistrictsChange}
                      width={600}
                      showSelectAll
                      selectAllText="All districts"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Provinces are shown as headers; click one to select all
                      its districts, or pick individual districts.
                    </Typography>
                    {geographyActive && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={handleClearGeography}
                      >
                        Clear geography filter
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item md={12} xs={12}>
                  <Box pt={3}>
                    <VirtualizedMultiSelect
                      label="Products"
                      id="products-virtualized"
                      value={productselected}
                      options={productOptions}
                      onChange={handleProductChange}
                      width={600}
                      showSelectAll
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box pt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={!submitEnabled}
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </form>
  );
}
